import { useMemo, useState, type ReactNode } from 'react'
import { Box, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'
import { Card, Skeleton } from '@repo/ui'

import { Header } from 'components'
import { TrendChart } from 'components/analytics'
import { SegmentedTabs } from 'components/navigation'
import { useCanAccess, useWebsiteContent } from 'hooks'
import type { WebsiteContentRow } from 'services'

import { ARTICLES_MANIFEST } from '../../GtmArticlesPage/articlesManifest.generated'
import { Cell, EmptyRow, HeadCell, Notice, SectionNote, SourceNotices, StatTable, useTone } from '../../InternalAnalyticsPage/components/shared'
import { InsightsSpendCard } from './InsightsSpendCard'
import {
  COLOUR_MIN_VIEWS,
  dayRange,
  duration,
  fillContentDays,
  isInterviewKind,
  normalisePath,
  percent,
  rate,
  readThrough,
  relativeTone,
  signupRate,
  type Rate,
} from './contentAnalyticsModel'

const RANGES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '12 months' },
]

const DESCRIPTION =
  'How each /insights piece is read, from visitors who accepted analytics only. Every figure shows its n. Below 100 views a rate has no colour, and with colour it compares pieces with each other, not with a target.'

const KIND_LABEL: Record<string, string> = { article: 'Article', video: 'Interview', explainer: 'Explainer' }

/**
 * The piece column's cap. 200, down from 260: at 1280 with the sidebar EXPANDED the content area
 * is 893px and this table measures 1007, so the last columns — Signed up and Contact, the two a
 * reader comes here for — sit outside it and need a sideways scroll. 200 takes 60px off that.
 *
 * It does not close the gap, and the gap is NOT new: "Get started", the header this column
 * replaced, measures 61px against "Signed up"'s 52 in the same font, so the table was at least
 * this wide before. The wrapper is `overflow-x: auto`, so nothing is unreachable. Making the
 * table actually fit means dropping a measure, which is a product decision — VRG-766.
 */
const PIECE_WIDTH = 200
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

interface Piece {
  path: string
  title: string
  kind: string
  inManifest: boolean
  stats: WebsiteContentRow | null
}

/** A rate and its n, coloured only when it has earned it (see relativeTone). */
const RateCell = ({ r, views, peers }: { r: Rate; views: number; peers: number[] }) => {
  const tone = useTone()
  const t = relativeTone(r.share, views, peers)
  return (
    <Cell align="right">
      <Box component="span" sx={{ color: t === 'neutral' ? 'text.primary' : tone(t).color, fontWeight: t === 'neutral' ? 400 : 600 }}>
        {percent(r.share)}
      </Box>
      <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
        n={r.n}
      </Box>
    </Cell>
  )
}

/**
 * VRG-765 — the SIGN-UP cell: the count, with the opens it came out of as its n.
 *
 * It carries the denominator in the table's OWN idiom (`9  n=23`, as Median time and both rate
 * cells do) rather than on a second line reading "of 23 opens · 39%". That first attempt was
 * measured at 1280 with the sidebar open and pushed the Contact column off the right edge —
 * a two-line cell is wide, and a header you cannot see is a column whose meaning is gone (§49).
 * The note above the table says what the n is.
 */
const SignupCell = ({ r }: { r: Rate }) => (
  <Cell align="right">
    <Box component="span" sx={{ fontWeight: r.hits > 0 ? 600 : 400 }}>
      {r.hits}
    </Box>
    <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
      n={r.n}
    </Box>
  </Cell>
)

/** The piece's title over its type and path, capped so the figures fit at 1280. */
const PieceCell = ({ p }: { p: Piece }) => (
  <Cell>
    <Box sx={{ maxWidth: PIECE_WIDTH, ...ellipsis }} title={p.title}>
      {p.title}
    </Box>
    <Typography variant="body4" color="text.secondary" component="div" sx={{ maxWidth: PIECE_WIDTH, ...ellipsis }} title={p.path}>
      {p.inManifest ? `${KIND_LABEL[p.kind] ?? p.kind} · ${p.path}` : 'Not in the articles manifest'}
    </Typography>
  </Cell>
)

const SkeletonRows = ({ cols }: { cols: number }) =>
  Array.from({ length: 5 }, (_, i) => (
    <TableRow key={i}>
      {Array.from({ length: cols }, (__, j) => (
        <Cell key={j}>
          <Skeleton height={14} width={j === 0 ? 220 : 48} />
        </Cell>
      ))}
    </TableRow>
  ))

const NotMeasured = ({ title, children }: { title: string; children: ReactNode }) => (
  <Stack spacing={1}>
    <Typography variant="body3" sx={{ fontWeight: 500 }}>
      {title}
    </Typography>
    <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
      {children}
    </Typography>
  </Stack>
)

/**
 * VRG-734 step 4 — GTM → Insights → Analytics. Per piece: reach, median time, scroll reach and
 * on-page outcomes, from PostHog's consent-gated events (no second analytics system).
 * What the website does not send yet (section reach, every video measure) is SAID, never shown
 * as a zero.
 *
 * VRG-765 (Spencer, 2026-10-29) adds three things and refuses a fourth:
 *
 *  · A SIGN-UP column on both blocks — `request_access_submitted`, the Get started form going
 *    through. It attributes to the piece because the form is a modal that pushes no URL
 *    (vera-website `app/@chrome/default.tsx`), so the submit carries the article's own pathname.
 *    Verified in the website source before the column was built, not assumed: a column reading
 *    0 everywhere because the event fires on another path would be a lie told quietly.
 *
 *  · Two TREND CHARTS, views per day and sign-ups per day, in the house `TrendChart`. SEPARATE
 *    charts, not one with two y-axes: views run ~7 a day and sign-ups ~0.1, and a shared axis
 *    would flatten the sign-up line onto the floor while a twin axis invites a comparison of two
 *    unrelated scales. Each owns its magnitude.
 *
 *  · An INTERVIEWS block of its own, split out by the manifest's `kind` (`video`). Scroll reach
 *    is not in it: depth-of-scroll on a page whose content is a video measures nothing.
 *
 *  · It does NOT show minutes watched, plays, completion, or reel and clip reach. None of it is
 *    measured — the site has no player and the Instagram publisher reads nothing back (it posts
 *    only; `features/gtm/social-publish` has no insights call). Those sit in the Interviews block
 *    as named gaps with the event or API each needs, where the subject is, rather than as zeros
 *    that would read as "nobody watched".
 */
export const ContentAnalyticsPanel = ({ title, tabs }: { title: string; tabs: ReactNode }) => {
  const [days, setDays] = useState('90')
  const range = useMemo(() => dayRange(Number(days)), [days])
  const { isHasAccess: canSee } = useCanAccess({ action: 'internal_analytics_view' })
  const { data, isLoading, error } = useWebsiteContent(range, canSee)

  const pieces: Piece[] = useMemo(() => {
    const byPath = new Map((data?.rows ?? []).map((r) => [normalisePath(r.path), r]))
    const out: Piece[] = ARTICLES_MANIFEST.articles
      .filter((a) => a.section === 'insights')
      .map((a) => {
        const key = normalisePath(a.path)
        const stats = byPath.get(key) ?? null
        byPath.delete(key)
        return { path: a.path, title: a.title, kind: a.kind, inManifest: true, stats }
      })
    // A path PostHog saw that the manifest does not list (renamed, removed, or newer than it).
    for (const [path, stats] of byPath) out.push({ path, title: path, kind: '', inManifest: false, stats })
    return out.sort((a, b) => (b.stats?.views ?? 0) - (a.stats?.views ?? 0))
  }, [data])

  const interviews = pieces.filter((p) => isInterviewKind(p.kind))
  const articles = pieces.filter((p) => !isInterviewKind(p.kind))

  // Colour compares a piece with its peers, and the peer set is EVERY piece above the line: the
  // interviews block is too small to be its own comparison group and would colour on noise.
  const eligible = pieces.filter((p) => (p.stats?.views ?? 0) >= COLOUR_MIN_VIEWS && p.stats)
  const peers = (pick: (r: WebsiteContentRow) => number | null) =>
    eligible.map((p) => pick(p.stats as WebsiteContentRow)).filter((v): v is number => v !== null)
  const readPeers = peers((r) => readThrough(r).share)
  const reach50Peers = peers((r) => rate(r.reached.p50, r.scrolledViews).share)

  const totals = useMemo(() => {
    const rows = data?.rows ?? []
    return {
      views: rows.reduce((t, r) => t + r.views, 0),
      subscribed: rows.reduce((t, r) => t + r.outcomes.subscribed, 0),
      getStarted: rows.reduce((t, r) => t + r.outcomes.getStartedOpened, 0),
      signedUp: rows.reduce((t, r) => t + r.outcomes.getStartedSubmitted, 0),
      contact: rows.reduce((t, r) => t + r.outcomes.contactSubmitted, 0),
    }
  }, [data])

  /**
   * `TrendChart` takes rows keyed by its `dataKey`s, so the series is mapped to that shape rather
   * than cast to it — a cast would let a renamed field through silently and draw an empty chart.
   */
  const series = useMemo<Array<Record<string, string | number | null>> | null>(
    () =>
      data?.days
        ? fillContentDays(data.days, range).map((d) => ({
            day: d.day,
            views: d.views,
            subscribed: d.subscribed,
            getStartedSubmitted: d.getStartedSubmitted,
          }))
        : null,
    [data, range],
  )

  // A failed request or a failed source is MISSING: never rows of zeros.
  const missing = !!error || (!!data && data.rows === null)

  return (
    <>
      <Header title={title} description={DESCRIPTION} />
      {tabs}
      {canSee ? <InsightsSpendCard /> : null}
      <Stack spacing={5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <SegmentedTabs tabs={RANGES} value={days} onChange={setDays} />
          {data && !missing && (
            <Typography variant="body3" color="text.secondary">
              {totals.views} views · {totals.subscribed} newsletter sign-ups · {totals.signedUp} signed up of {totals.getStarted} Get
              started opens · {totals.contact} contact forms
            </Typography>
          )}
        </Stack>

        {!canSee ? (
          <Notice severity="info">Content analytics needs the Website analytics permission. Ask an admin to grant it.</Notice>
        ) : error ? (
          <Notice severity="error">{(error as Error).message}</Notice>
        ) : (
          data && <SourceNotices sources={[['Visitors with analytics on', data.posthog]]} />
        )}

        <Card title="Over time">
          <Stack spacing={3}>
            <SectionNote>
              All /insights pages together, per day. A sign-up here is a Get started form submitted while on an /insights page. Someone
              who reads a piece and signs up later from another page is not counted, and neither are newsletter sign-ups.
            </SectionNote>
            {isLoading && canSee ? (
              <Skeleton height={140} />
            ) : !series || !canSee ? (
              <SectionNote>No series: the daily read is unavailable, so this is missing, not zero.</SectionNote>
            ) : (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body3" sx={{ fontWeight: 500, mb: 2 }}>
                    Views per day
                  </Typography>
                  <TrendChart data={series} xKey="day" series={[{ dataKey: 'views', label: 'Views' }]} ariaLabel="Views per day" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body3" sx={{ fontWeight: 500, mb: 2 }}>
                    Sign-ups on /insights pages per day
                  </Typography>
                  {/* Bars, not an area: whole counts, mostly 0 or 1, and a curve through them
                      would draw halves of a sign-up on the days between. */}
                  <TrendChart
                    data={series}
                    xKey="day"
                    mark="bar"
                    series={[{ dataKey: 'getStartedSubmitted', label: 'Signed up' }]}
                    ariaLabel="Sign-ups on /insights pages per day"
                  />
                </Box>
              </Stack>
            )}
          </Stack>
        </Card>

        <Card title="Articles">
          <Stack spacing={3}>
            <SectionNote>
              Median time is over views something closed (a next page or leaving the site). Scroll reach and read-through (90% of the
              page) are over the closed views that reported how far they scrolled. n is that denominator. Signed up is the Get started
              form submitted while on the piece, and its n is the opens it came out of.
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Piece</HeadCell>
                  <HeadCell align="right">Views</HeadCell>
                  <HeadCell align="right">Readers</HeadCell>
                  <HeadCell align="right">Median time</HeadCell>
                  <HeadCell align="right">Reached half</HeadCell>
                  <HeadCell align="right">Read-through</HeadCell>
                  <HeadCell align="right">Newsletter</HeadCell>
                  <HeadCell align="right">Signed up</HeadCell>
                  <HeadCell align="right">Contact</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && canSee ? (
                  <SkeletonRows cols={9} />
                ) : missing || !canSee ? (
                  <EmptyRow colSpan={9} text="No figures: the analytics source is unavailable, so these are missing, not zero." />
                ) : articles.length === 0 ? (
                  <EmptyRow colSpan={9} text="No articles in the manifest yet." />
                ) : (
                  articles.map((p) => {
                    const s = p.stats
                    return (
                      <TableRow key={p.path}>
                        <PieceCell p={p} />
                        <Cell align="right" strong>
                          {s ? s.views : 0}
                        </Cell>
                        <Cell align="right">{s ? s.visitors : 0}</Cell>
                        <Cell align="right">
                          {duration(s?.medianSeconds ?? null)}
                          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                            n={s?.timedViews ?? 0}
                          </Box>
                        </Cell>
                        <RateCell r={rate(s?.reached.p50 ?? 0, s?.scrolledViews ?? 0)} views={s?.views ?? 0} peers={reach50Peers} />
                        <RateCell r={s ? readThrough(s) : rate(0, 0)} views={s?.views ?? 0} peers={readPeers} />
                        <Cell align="right">{s?.outcomes.subscribed ?? 0}</Cell>
                        <SignupCell r={s ? signupRate(s) : rate(0, 0)} />
                        <Cell align="right">{s?.outcomes.contactSubmitted ?? 0}</Cell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </StatTable>
          </Stack>
        </Card>

        <Card title="Interviews">
          <Stack spacing={4}>
            <SectionNote>
              VERAfied episodes published to /insights. These are PAGE measures — the same ones the articles get — so they say how the
              episode page performed, not how the video did. Signed up is the Get started form submitted while on the episode, over
              the opens it came out of. Scroll reach is left out on purpose: depth-of-scroll on a page whose content is a video
              measures nothing.
            </SectionNote>
            <StatTable>
              <TableHead>
                <TableRow>
                  <HeadCell>Episode</HeadCell>
                  <HeadCell align="right">Views</HeadCell>
                  <HeadCell align="right">Viewers</HeadCell>
                  <HeadCell align="right">Median time</HeadCell>
                  <HeadCell align="right">Newsletter</HeadCell>
                  <HeadCell align="right">Signed up</HeadCell>
                  <HeadCell align="right">Contact</HeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && canSee ? (
                  <SkeletonRows cols={7} />
                ) : missing || !canSee ? (
                  <EmptyRow colSpan={7} text="No figures: the analytics source is unavailable, so these are missing, not zero." />
                ) : interviews.length === 0 ? (
                  <EmptyRow
                    colSpan={7}
                    text="No episode is published to /insights yet. Rows appear here on their own once one is — nothing else to wire."
                  />
                ) : (
                  interviews.map((p) => {
                    const s = p.stats
                    return (
                      <TableRow key={p.path}>
                        <PieceCell p={p} />
                        <Cell align="right" strong>
                          {s ? s.views : 0}
                        </Cell>
                        <Cell align="right">{s ? s.visitors : 0}</Cell>
                        <Cell align="right">
                          {duration(s?.medianSeconds ?? null)}
                          <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                            n={s?.timedViews ?? 0}
                          </Box>
                        </Cell>
                        <Cell align="right">{s?.outcomes.subscribed ?? 0}</Cell>
                        <SignupCell r={s ? signupRate(s) : rate(0, 0)} />
                        <Cell align="right">{s?.outcomes.contactSubmitted ?? 0}</Cell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </StatTable>
            <NotMeasured title="Plays, minutes watched, completion and the retention curve">
              Not measured, and shown as missing rather than as zeros — a zero here would read as "nobody watched". The site has no
              video player yet (the interview slot is a placeholder) and no episode is published. Once a player exists it needs to send
              play, pause, ended and the 10% marks; minutes watched is then the sum of watched seconds, and completion the share of
              plays that reached the end.
            </NotMeasured>
            <NotMeasured title="Reels and clips: reach, plays and watch time off-platform">
              Not measured. Vera's Instagram integration publishes only. It posts a reel and records that it posted, and never reads
              anything back, so there is no view, reach or watch-time figure to aggregate, for reels or for the short clips cut from
              an episode. It needs the Instagram Graph insights read, and the YouTube and Spotify equivalents. Each is its own
              integration rather than a column here.
            </NotMeasured>
          </Stack>
        </Card>

        <Card title="Not measured yet">
          <Stack spacing={4}>
            <SectionNote>
              These need events the website does not send yet. They are listed here rather than shown as zeros.
            </SectionNote>
            <NotMeasured title="Section reach (articles)">
              The share of readers who reached each h2. The article template already has stable h2 ids; the site needs to send an event
              when each one comes into view.
            </NotMeasured>
            <NotMeasured title="Related-rail and series-page clicks">The site needs to send a click event for each.</NotMeasured>
          </Stack>
        </Card>
      </Stack>
    </>
  )
}
